import { Routes } from '@angular/router';
import { LandingLayoutComponent } from './layout/landing-layout/landing-layout.component';
import { autoLoginPartialRoutesGuard } from 'angular-auth-oidc-client';
import { MainLayoutComponent } from './layout/main-layout/main-layout.component';
import { Constants } from './common/helper/Constants';
import { VersionLogComponent } from './feature/version-log/version-log.component';
import { CHANGELOG_EDITOR_ROUTES } from 'changelog-editor';
import { roleGuard } from './core/auth/role.guard';
import { Role } from './core/auth/role.enum';
import { FallbackComponent } from './feature/fallback/fallback.component';
import { ForbiddenComponent } from './feature/forbidden/forbidden.component';
import { MasterDataComponent } from './feature/master-data/master-data.component';
import { autoRedirectGuard } from './core/auth/auto-redirect.guard';
import { CustomerImpersonationComponent } from './feature/acp-customers/customer-impersonation/customer-impersonation.component';
import { AcpLaunchpadComponent } from './feature/acp-launchpad/acp-launchpad.component';
import { ServiceCaseOverviewComponent } from './feature/service-case/service-case-overview/service-case-overview.component';
import { ModuleOverviewComponent } from './feature/acp-customers/module-management/module-overview/module-overview.component';
import { CustomerAssignmentMissingComponent } from './feature/customer-assignment-missing/customer-assignment-missing/customer-assignment-missing.component';
import { CustomerAccessGuard } from './core/auth/customer-access.guard';
import { CustomerAssignmentMissingGuard } from './core/auth/customer-assign-missing.guard';
import { UserDirectoryComponent } from './common/component/user-directory/user-directory.component';
import { ExternalContractComponent } from './feature/external-contract/external-contract.component';
import { UserDirectoryDetailComponent } from './common/component/user-directory/user-directory-detail/user-directory-detail.component';
export const routes: Routes = [
  {
    path: '',
    component: LandingLayoutComponent,
    canActivate: [autoRedirectGuard],
  },
  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [
      autoLoginPartialRoutesGuard,
      CustomerAccessGuard
    ],
    children: [
      {
        path: Constants.routes.homePath,
        loadChildren: () => import('./feature/home/home.routes'),
        title: 'routeTitle.dashboard',
        data: { breadcrumb: 'dashboard' }
      },
      {
        path: Constants.routes.offerPath,
        loadChildren: () => import('./feature/offer/offer.routes'),
        title: 'routeTitle.offers',
        data: {
          sectionBreadcrumb: 'documents',
          breadcrumb: 'offers'
        }
      },
      {
        path: Constants.routes.orderPath,
        loadChildren: () => import('./feature/order/order.routes'),
        title: 'routeTitle.orders',
        data: {
          sectionBreadcrumb: 'documents', breadcrumb: 'orders'
        }
      },
      {
        path: Constants.routes.invoicePath,
        loadChildren: () => import('./feature/invoice/invoice.routes'),
        title: 'routeTitle.invoices',
        data: {
          sectionBreadcrumb: 'documents', breadcrumb: 'invoices'
        }
      },
      {
        path: Constants.routes.deliveryNotePath,
        loadChildren: () => import('./feature/delivery-note/delivery-note.routes'),
        title: 'routeTitle.deliveryNotes',
        data: {
          sectionBreadcrumb: 'documents', breadcrumb: 'deliveryNotes'
        }
      },
      {
        path: Constants.routes.contractPath,
        loadChildren: () => import('./feature/contract/contract.routes'),
        title: 'routeTitle.contracts',
        data: {
          sectionBreadcrumb: 'documents', breadcrumb: 'contracts'
        }
      },
      {
        path: Constants.routes.externalContractPath,
        component: ExternalContractComponent,
        title: 'menu.externalContracts',
        data: {
          sectionBreadcrumb: 'documents', breadcrumb: 'externalContracts',
          required: [Role.GateExternalContractsRead],
          type: 'group'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.feedbackPath,
        loadChildren: () => import('./feature/feedback/feedback.routes'),
        title: 'routeTitle.feedback',
        data: {
          breadcrumb: 'feedback',
        },
      },
      {
        path: Constants.routes.legacyLicensePath,
        loadChildren: () => import('./feature/license/license.routes'),
        title: 'routeTitle.licenses',
        data: {
          sectionBreadcrumb: 'product', breadcrumb: 'licenses',
          required: [Role.GateLicensesRead],
          type: 'group'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.licensePath,
        loadChildren: () => import('./feature/license-v2/license.routes'),
        title: 'routeTitle.licenses',
        data: {
          sectionBreadcrumb: 'product', breadcrumb: 'licenses',
          required: [Role.GateLicensesWrite, Role.GateLicensesRead],
          type: 'group',
          match: 'any'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.creditPath,
        loadChildren: () => import('./feature/credit/credit.routes'),
        title: 'routeTitle.serviceCredit',
        data: {
          sectionBreadcrumb: 'service', breadcrumb: 'serviceCredit',
          required: [Role.GateBalanceRead, Role.GateBalanceWriteInfo],
          type: 'group',
          match: 'any'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.inventoryPath,
        loadChildren: () => import('./feature/inventory/inventory.routes'),
        title: 'routeTitle.stockLevel',
        data: {
          sectionBreadcrumb: 'reports', breadcrumb: 'stockLevel',
          required: [Role.GateStockRead],
          type: 'group'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.serviceReportPath,
        loadChildren: () => import('./feature/service-report/service-report.routes'),
        title: 'routeTitle.serviceReports',
        data: {
          sectionBreadcrumb: 'reports', breadcrumb: 'serviceReports',
          required: [Role.GateServiceReportsRead],
          type: 'group'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.serviceCasePath,
        title: 'routeTitle.serviceCases',
        data: {
          sectionBreadcrumb: 'service', breadcrumb: 'serviceCases'
        },
        component: ServiceCaseOverviewComponent,
      },

      {
        path: Constants.routes.modulePath,
        title: 'routeTitle.modules',
        data: {
          breadcrumb: 'module',
          required: [Role.AccountManager, Role.ModuleAdmin]
        },
        canActivate: [roleGuard],
        component: ModuleOverviewComponent

      },

      {
        path: 'admin-changelog',
        title: 'routeTitle.changelog',
        data: {
          breadcrumb: 'changelog',
          required: [Role.ModuleAdmin]
        },
        canActivate: [roleGuard],
        canActivateChild: [roleGuard],
        children: CHANGELOG_EDITOR_ROUTES
      },

      {
        path: Constants.routes.customersPath,
        loadChildren: () => import('./feature/acp-customers/acp-customers.routes'),
        title: 'routeTitle.customerOverview',
        data: {
          breadcrumb: 'customerOverview',
          required: [Role.AccountManager]
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.userAdministrationDetailPath,
        component: UserDirectoryDetailComponent,
        data: {
          sectionBreadcrumb: 'userAdministration',
          sectionBreadcrumbUrl: Constants.routes.userAdministration(),
          breadcrumb: 'customerOverview',
          showParamBreadcrumb: false,
          required: [Role.AccountManager],
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.userAdministrationPath,
        component: UserDirectoryComponent,
        data: {
          sectionBreadcrumb: 'userAdministration',
          breadcrumb: 'userAdministration',
          required: [Role.AccountManager],
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.usersPath,
        loadChildren: () => import('./feature/user-management/user-management.routes'),
        title: 'routeTitle.users',
        data: {
          sectionBreadcrumb: 'userManagement',
          breadcrumb: 'users',
          required: [Role.Customer],
          groupRequiredAny: [Role.GateGroupAdmin, Role.GateGroupRead],
          type: 'both',
          match: 'any'
        },
        canActivate: [roleGuard]
      },
      {
        path: Constants.routes.marketplacePath,
        loadChildren: () => import('./feature/marketplace/marketplace.routes'),
        title: 'routeTitle.marketplace',
        data: { sectionBreadcrumb: 'product', breadcrumb: 'marketplace' }
      },
      {
        path: Constants.routes.shopPath,
        loadChildren: () => import('./feature/shop/shop.routes'),
        title: 'routeTitle.shop',
        data: {
          sectionBreadcrumb: 'shop',
          sectionBreadcrumbUrl: Constants.routes.catalogue(),
        },
      },
      {
        path: Constants.routes.customerImpersonationPath,
        component: CustomerImpersonationComponent,
        title: 'routeTitle.dashboard',
        data: { breadcrumb: 'dashboard' }
      },
      {
        path: Constants.routes.versionLogPath,
        component: VersionLogComponent,
        title: 'routeTitle.version',
        data: { breadcrumb: 'version' }
      },
      {
        path: Constants.routes.customerMasterDataPath,
        title: 'routeTitle.masterData',
        data: {
          breadcrumb: 'masterData',
          required: [Role.GateMasterDataRead, Role.GateMasterDataWrite, Role.GateMasterDataWriteAll],
          type: 'both',
          match: 'any',
          acpUserBypass: true,
        },
        component: MasterDataComponent,
        canActivate: [roleGuard]
      },
      { path: Constants.routes.forbiddenPath, component: ForbiddenComponent, title: 'routeTitle.forbidden' },
      {
        path: Constants.routes.launchpadPath,
        title: 'routeTitle.launchpad',
        data: {
          breadcrumb: 'launchpad',
          required: [Role.AccountManager]
        },
        component: AcpLaunchpadComponent,
        canActivate: [roleGuard]
      },
    ],
  },

  {
    path: Constants.routes.customerAssignmentMissingPath,
    component: CustomerAssignmentMissingComponent,
    title: 'routeTitle.noCustomer',
    canActivate: [CustomerAssignmentMissingGuard]
  },
  {
    path: '**',
    component: FallbackComponent,
    title: 'routeTitle.notFound',
  },
];
